import {
    MORNING_BRIEFING_START_HOUR,
    MORNING_BRIEFING_END_HOUR,
    EVENING_BRIEFING_START_HOUR,
    EVENING_BRIEFING_END_HOUR,
    FORCE_MORNING_BRIEFING_START_HOUR,
    FORCE_MORNING_BRIEFING_END_HOUR,
    FORCE_EVENING_BRIEFING_START_HOUR,
    FORCE_EVENING_BRIEFING_END_HOUR,
} from './constants';
import { userDate, getHours, startOfDay, addHours, addMinutes, getTimestamp } from 'owa-datetime';

// The nudge is kept on screen for an extra minute past the technical window end
// so that a nudge rendered right before the boundary doesn't vanish before the
// user can see and interact with it.
const BRIEFING_WINDOW_GRACE_MINUTES = 1;

/**
 * Checks if current time is within the morning briefing window.
 * @param useForceWindow - When true, uses the wider force window instead of the normal window.
 * @returns true if current hour is between the morning briefing hours
 */
export function isMorningBriefing(useForceWindow?: boolean): boolean {
    const now = userDate();
    const hour = getHours(now);

    if (useForceWindow) {
        return hour >= FORCE_MORNING_BRIEFING_START_HOUR && hour < FORCE_MORNING_BRIEFING_END_HOUR;
    }
    return hour >= MORNING_BRIEFING_START_HOUR && hour < MORNING_BRIEFING_END_HOUR;
}

/**
 * Checks if current time is within the evening briefing window
 * @param useForceWindow - When true, uses the wider force window instead of the normal window.
 * @returns true if current hour is between the evening briefing hours
 */
export function isEveningBriefing(useForceWindow?: boolean): boolean {
    const now = userDate();
    const hour = getHours(now);
    if (useForceWindow) {
        return hour >= FORCE_EVENING_BRIEFING_START_HOUR && hour < FORCE_EVENING_BRIEFING_END_HOUR;
    }

    return hour >= EVENING_BRIEFING_START_HOUR && hour < EVENING_BRIEFING_END_HOUR;
}

/**
 * Returns the active briefing window's end as an absolute epoch-ms timestamp
 * (including the grace minute), or `undefined` if no briefing window is
 * currently active.
 *
 * Unlike a relative "ms from now" value, this absolute boundary is stable across
 * renders, so callers (e.g. MailListNudgeHost) can schedule a single re-render
 * at this instant to drop the nudge when the window closes.
 */
export function getBriefingWindowEndTimestamp(): number | undefined {
    const now = userDate();
    const hour = getHours(now);

    let endHour: number | undefined;
    if (hour >= MORNING_BRIEFING_START_HOUR && hour < MORNING_BRIEFING_END_HOUR) {
        endHour = MORNING_BRIEFING_END_HOUR;
    } else if (hour >= EVENING_BRIEFING_START_HOUR && hour < EVENING_BRIEFING_END_HOUR) {
        endHour = EVENING_BRIEFING_END_HOUR;
    }

    if (endHour === undefined) {
        return undefined;
    }

    const windowEnd = addMinutes(addHours(startOfDay(now), endHour), BRIEFING_WINDOW_GRACE_MINUTES);
    return getTimestamp(windowEnd);
}

/**
 * Returns the number of milliseconds until the active briefing window ends
 * (including the grace minute), or 0 if no briefing window is currently active.
 */
export function getMsUntilBriefingWindowEnds(): number {
    const windowEnd = getBriefingWindowEndTimestamp();
    if (windowEnd === undefined) {
        return 0;
    }
    return Math.max(0, windowEnd - getTimestamp(userDate()));
}
